'use client';

import { cn } from '../../../../lib/utils';
import { Checkbox } from '../../../primitives/controls';
import { LoadingSpinner } from '../../../primitives/feedback-and-status';
import { ProcessingIndicatorLoadingSpinner } from '../../overlays';
import {
  AudioFileFill,
  Bookmark,
  BookmarkFill,
  StickyNoteFill,
} from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';

export interface ContentCardProps extends Omit<
  React.ComponentProps<'div'>,
  'title'
> {
  /** 컨텐츠 상태 */
  state?: 'default' | 'selectable' | 'failed' | 'loading' | 'expired';
  /** 시각적 상태 (selectable 상태에서도 오버레이/라벨을 보여주기 위함) */
  visualState?: 'default' | 'failed' | 'loading' | 'expired';
  /** 로딩 타입 */
  loadingType?: 'infinite' | 'percentage' | 'text' | 'none';
  /** 컨텐츠 타입 */
  type?: 'default' | 'font' | 'audio';
  /** 컨텐츠 제목 */
  title?: string | React.ReactNode;
  /** 컨텐츠 메타데이터 */
  metadata?: string | React.ReactNode;
  /** 컨텐츠 이미지 노드 */
  mediaNode?: React.ReactNode;
  /** 컨텐츠 메뉴 트리거 노드 */
  menuTriggerNode?: React.ReactNode;
  /** 메타데이터 라벨 노드 */
  metadataLabelNode?: React.ReactNode;
  /** selectable 상태에서 선택 여부 */
  isSelected?: boolean;
  /** selectable 상태에서 선택 변경 이벤트 */
  onSelectedChange?: (selected: boolean) => void;
  /** Failed 상태 라벨 노드 (썸네일 하단 중앙) */
  failedLabelNode?: React.ReactNode;
  /** Expired 상태 라벨 노드 (썸네일 하단 중앙) */
  expiredLabelNode?: React.ReactNode;
  /** 퍼센트 값 */
  percentageValue?: number;
  /** 퍼센트 값 라벨 노드 */
  percentageLabelNode?: React.ReactNode;
  /** 컨텐츠 비디오 or 이미지 영역 클레스 추가 옵션 */
  mediaNodeClassName?: string;
  /** 컨텐츠 영역 Badge 노드 */
  badgeNode?: React.ReactNode;
  /** 컨텐츠 영역 Badge 노드 표시 여부 */
  isShowBadgeNode?: boolean;
  /** active 효과 표시 여부 */
  isActiveCssEvent?: boolean;
  /** 포커스 상태 여부 */
  isFocused?: boolean;
  /** 퍼센트 텍스트 크기 */
  percentageFontSize?: number;
  /** 북마크 버튼 표시 여부 */
  isShowBookmark?: boolean;
  /** 북마크 상태 */
  isBookmarked?: boolean;
  /** 북마크 클릭 핸들러 */
  onBookmarkClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 썸네일 영역 우측 상단 타입 라벨 노드 (LTS/MTS/비디오 등) */
  typeLabelNode?: React.ReactNode;
  /** 타입 라벨 표시 여부 */
  isShowTypeLabel?: boolean;
}

const contentCardVariants = cva(
  [
    'flex flex-col w-full rounded-sm transition-all p-4xs',
    'hover:bg-sys-bg-surface-hover',
  ],
  {
    variants: {
      isActiveCssEvent: {
        true: 'active:bg-sys-bg-surface-press',
      },
    },
  },
);

export default function ContentCard({
  state = 'default',
  visualState,
  type = 'default',
  loadingType = 'infinite',
  title,
  metadata,
  mediaNode,
  menuTriggerNode,
  metadataLabelNode = true,
  className,
  isSelected = false,
  onSelectedChange,
  failedLabelNode,
  expiredLabelNode,
  percentageValue,
  percentageLabelNode,
  mediaNodeClassName,
  children,
  badgeNode,
  isShowBadgeNode = false,
  isActiveCssEvent = true,
  isFocused = false,
  percentageFontSize = 12,
  isShowBookmark = false,
  isBookmarked = false,
  onBookmarkClick,
  typeLabelNode,
  isShowTypeLabel = false,
  ...props
}: ContentCardProps) {
  // visualState가 없으면 state를 사용 (selectable일 때는 default)
  const effectiveVisualState =
    visualState ?? (state === 'selectable' ? 'default' : state);
  return (
    <div
      className={cn(
        contentCardVariants({ isActiveCssEvent }),
        className,
        isFocused && 'bg-sys-bg-surface-primary',
        state === 'selectable' && isSelected && 'bg-sys-bg-surface-primary',
        state === 'selectable' &&
          isSelected &&
          'border-[2px] border-sys-style-primary-default',
      )}
      {...props}
    >
      {/* 컨텐츠 영역 */}
      <div className="relative h-full w-full overflow-hidden rounded-xs">
        <div
          className={cn(
            'relative h-full w-full overflow-hidden rounded-xs transition-all',
            'aspect-video',
            mediaNodeClassName,
          )}
        >
          {/* Failed 일 경우 보여지는 배경 투명도 지정 */}
          {effectiveVisualState === 'failed' && (
            <div
              className={cn(
                'absolute z-20 h-full w-full bg-comp-label-red-bg opacity-50',
              )}
            />
          )}
          {/* Expired 일 경우 보여지는 배경 투명도 지정 */}
          {effectiveVisualState === 'expired' && (
            <div
              className={cn(
                'absolute z-20 h-full w-full bg-sys-style-neutral-default opacity-50',
              )}
            />
          )}
          {/* Loading 일 경우 보여지는 배경 투명도 지정 */}
          {effectiveVisualState === 'loading' && (
            <div className="absolute z-20 h-full w-full bg-sys-style-neutral-lightest opacity-70" />
          )}
          {/* Loading 일 경우 보여지는 배경 투명도 지정 */}
          {effectiveVisualState === 'loading' && (
            <div className="absolute z-20 h-full w-full">
              <div className="flex h-full w-full flex-col items-center justify-center gap-2xs">
                {loadingType === 'percentage' && (
                  <ProcessingIndicatorLoadingSpinner
                    value={percentageValue}
                    size={55}
                    strokeWidth={6}
                    percentageFontSize={percentageFontSize}
                  />
                )}
                {loadingType === 'infinite' && (
                  <div className="mb-2xs">
                    <LoadingSpinner size="large" />
                  </div>
                )}
                {loadingType === 'text' && null}
                {loadingType === 'none' && null}
                {percentageLabelNode}
              </div>
            </div>
          )}
          {/* Image 일 경우 보여지는 이미지 지정 */}
          {mediaNode && (
            <div className="relative z-10 flex h-full w-full justify-center">
              {mediaNode}
            </div>
          )}
          {/* type이 default가 아닌 경우 type에 따른 배경이 보여지도록 지정 */}
          {type !== 'default' && (
            <div className="absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden bg-sys-style-neutral-light">
              {type === 'audio' && (
                <AudioFileFill className="size-[50px] [&_path]:fill-sys-cont-neutral-disabled-light" />
              )}
              {type === 'font' && (
                <StickyNoteFill className="size-[50px] [&_path]:fill-sys-cont-neutral-disabled-light" />
              )}
            </div>
          )}
          {/* Failed 상태 라벨 (썸네일 하단 중앙) */}
          {effectiveVisualState === 'failed' && failedLabelNode && (
            <div className="absolute right-0 bottom-2xs left-0 z-30 flex justify-center">
              {failedLabelNode}
            </div>
          )}
          {/* Expired 상태 라벨 (썸네일 하단 중앙) */}
          {effectiveVisualState === 'expired' && expiredLabelNode && (
            <div className="absolute right-0 bottom-2xs left-0 z-30 flex justify-center">
              {expiredLabelNode}
            </div>
          )}
        </div>
        {/* Metadata Label 일 경우 보여지는 메타데이터 라벨 지정 */}
        {metadataLabelNode && effectiveVisualState !== 'loading' && (
          <div className="relative z-30">{metadataLabelNode}</div>
        )}
        {/* Selectable 상태일 때 체크박스 표시 */}
        {state === 'selectable' && (
          <div
            className="absolute top-2xs left-2xs z-30"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              size="medium"
              checked={isSelected}
              onCheckedChange={onSelectedChange}
            />
          </div>
        )}
        {isShowBadgeNode && badgeNode && badgeNode}
        {/* 타입 라벨 (썸네일 우측 상단) */}
        {isShowTypeLabel && typeLabelNode && (
          <div className="absolute top-2xs right-2xs z-30">{typeLabelNode}</div>
        )}
      </div>
      {/* Title 및 Metadata 지정 */}
      {title || metadata || children ? (
        <div className="flex flex-col p-2xs">
          {/* Title 및 Menu Trigger 지정 */}
          {title && (
            <div className="flex items-center gap-4xs">
              <div
                className={
                  'min-w-0 flex-1 overflow-hidden text-title-small overflow-ellipsis whitespace-nowrap text-sys-cont-neutral-default'
                }
              >
                {title}
              </div>
              {isShowBookmark && (
                <button
                  type="button"
                  className="flex shrink-0 cursor-pointer items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onBookmarkClick?.(e);
                  }}
                  aria-label={isBookmarked ? '북마크 해제' : '북마크'}
                >
                  {isBookmarked ? (
                    <BookmarkFill className="size-5 [&_path]:fill-sys-cont-primary-default" />
                  ) : (
                    <Bookmark className="size-5 [&_path]:fill-sys-cont-neutral-default" />
                  )}
                </button>
              )}
              {menuTriggerNode}
            </div>
          )}
          {/* Metadata 지정 */}
          {metadata && (
            <div className="flex items-center gap-4xs">
              <div
                className={`text-caption-small ${
                  effectiveVisualState === 'failed'
                    ? 'text-sys-cont-negative-default'
                    : 'text-sys-cont-neutral-lightest'
                }`}
              >
                {metadata}
              </div>
            </div>
          )}
          {children}
        </div>
      ) : null}
    </div>
  );
}
